'use client';
import React from 'react';
import { safeSanitize } from '@/core/utils/sanitize';
import { cn } from '@/core/utils/cn';
import { reactElementToSvgString } from '@/core/utils/svg-stringify';
import { BlockProps } from './types';
import { useLocalizeHtml } from '@/core/i18n/navigation';
import { POPULAR_ICONS } from '../blockHelpers';

export function TextBlock({
  block,
  currentStyles,
  isAdmin,
  onUpdateBlockContent,
  innerElementStyles,
  resolveTags
}: BlockProps) {
  const localizeHtml = useLocalizeHtml();
  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (isAdmin && onUpdateBlockContent) {
      const newContent = e.currentTarget.innerHTML;
      if (newContent !== block.content) {
        onUpdateBlockContent(block.id, newContent);
      }
    }
  };

  const wrapperStyles = {
    ...innerElementStyles,
    // The block's own alignment (Tekst → Wyrównanie, per viewport); inline alignment set on a
    // paragraph in the rich-text editor still wins for that paragraph.
    textAlign: innerElementStyles.textAlign || 'inherit',
    wordBreak: 'break-word',
    overflowWrap: 'break-word'
  };

  // Custom list marker (icon) for <ul> lists
  const markerIconName = currentStyles.listMarkerType === 'icon' ? currentStyles.listMarkerIcon : null;
  const MarkerIcon = markerIconName ? POPULAR_ICONS[markerIconName] : null;
  const markerBlockClass = MarkerIcon ? `text-block-marker-${block.id}` : undefined;

  // Custom ordered list styling (01, 02, 03... / circle / square) for <ol> lists
  const orderedType = currentStyles.listOrderedType; // 'leading-zero' | 'circle' | 'square' | undefined
  const orderedBlockClass = orderedType ? `text-block-ordered-${block.id}` : undefined;

  const combinedCustomCss = React.useMemo(() => {
    let css = '';

    // Safety rule for pasted/flex HTML: ensure first-child numbers/icons never wrap or squish
    css += `
      .${cn(markerBlockClass, orderedBlockClass, `text-block-${block.id}`)} li[class*="flex"] > *:first-child,
      .${cn(markerBlockClass, orderedBlockClass, `text-block-${block.id}`)} li.flex > *:first-child,
      .${cn(markerBlockClass, orderedBlockClass, `text-block-${block.id}`)} [class*="flex"] > span:first-child {
        flex-shrink: 0 !important;
        white-space: nowrap !important;
      }
    `;

    if (MarkerIcon && markerBlockClass) {
      const size = currentStyles.listMarkerSize || 20;
      const bgEnabled = currentStyles.listMarkerBgEnabled === true;
      const iconColor = currentStyles.listMarkerColor || '#2563eb';
      const iconSize = bgEnabled ? Math.round(size * 0.6) : size;
      const svg = reactElementToSvgString(
        <MarkerIcon color={iconColor} width={iconSize} height={iconSize} strokeWidth={2.5} />
      );
      const iconUrl = `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
      css += `
        .${markerBlockClass} ul { list-style: none !important; padding-left: 0 !important; margin-left: 0 !important; }
        .${markerBlockClass} ul li {
          position: relative !important;
          list-style: none !important;
          padding-left: ${size + 8}px !important;
        }
        .${markerBlockClass} ul li::before {
          content: '' !important;
          display: inline-block !important;
          position: absolute !important;
          left: 0 !important;
          top: 0.15em !important;
          width: ${size}px !important;
          height: ${size}px !important;
          background-color: ${bgEnabled ? (currentStyles.listMarkerBgColor || '#eff6ff') : 'transparent'} !important;
          border-radius: ${bgEnabled ? '50%' : '0'} !important;
          background-image: url("${iconUrl}") !important;
          background-repeat: no-repeat !important;
          background-position: center !important;
          background-size: contain !important;
        }
      `;
    }

    if (orderedType && orderedBlockClass) {
      const numColor = currentStyles.listOrderedColor || '#FFCF00';
      const numSize = currentStyles.listOrderedSize || 20;
      const numWeight = currentStyles.listOrderedWeight || 800;
      const gap = currentStyles.listOrderedGap || 16;
      const itemMargin = currentStyles.listOrderedItemGap || 16;
      const isBadge = orderedType === 'circle' || orderedType === 'square';
      const bgColor = currentStyles.listOrderedBgColor || '#eff6ff';

      css += `
        .${orderedBlockClass} ol {
          list-style: none !important;
          counter-reset: stag-counter !important;
          padding-left: 0 !important;
          margin-left: 0 !important;
        }
        .${orderedBlockClass} ol li {
          counter-increment: stag-counter !important;
          display: flex !important;
          align-items: flex-start !important;
          gap: ${gap}px !important;
          margin-bottom: ${itemMargin}px !important;
          list-style: none !important;
          padding-left: 0 !important;
        }
        .${orderedBlockClass} ol li::before {
          content: counter(stag-counter, decimal-leading-zero) !important;
          font-family: inherit !important;
          font-weight: ${numWeight} !important;
          color: ${numColor} !important;
          font-size: ${numSize}px !important;
          line-height: 1.375 !important;
          flex-shrink: 0 !important;
          white-space: nowrap !important;
          ${isBadge ? `
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            width: ${numSize + 12}px !important;
            height: ${numSize + 12}px !important;
            border-radius: ${orderedType === 'circle' ? '50%' : '8px'} !important;
            background-color: ${bgColor} !important;
            font-size: ${Math.round(numSize * 0.75)}px !important;
          ` : ''}
        }
      `;
    }

    return css;
  }, [
    MarkerIcon,
    markerBlockClass,
    orderedType,
    orderedBlockClass,
    block.id,
    currentStyles.listMarkerSize,
    currentStyles.listMarkerBgEnabled,
    currentStyles.listMarkerColor,
    currentStyles.listMarkerBgColor,
    currentStyles.listOrderedColor,
    currentStyles.listOrderedSize,
    currentStyles.listOrderedWeight,
    currentStyles.listOrderedGap,
    currentStyles.listOrderedItemGap,
    currentStyles.listOrderedBgColor
  ]);

  const blockUniqueClass = `text-block-${block.id}`;

  return (
    <div
      className={cn(
        "prose prose-slate max-w-none text-current hover:text-[var(--hover-color)] transition-colors",
        blockUniqueClass,
        markerBlockClass,
        orderedBlockClass,
        // Direct heading styles to match editor's "super format"
        "prose-h1:text-[1.5em] prose-h1:font-bold prose-h1:mb-[0.1em] prose-h1:mt-0 prose-h1:text-inherit",
        "prose-h2:text-[1.25em] prose-h2:font-bold prose-h2:mb-[0.1em] prose-h2:mt-0 prose-h2:text-inherit",
        "prose-h3:text-[1.1em] prose-h3:font-bold prose-h3:mb-[0.1em] prose-h3:mt-0 prose-h3:text-inherit",
        "prose-h4:text-[1em] prose-h4:font-bold prose-h4:mb-[0.1em] prose-h4:mt-0 prose-h4:text-inherit",
        "prose-h5:text-[0.9em] prose-h5:font-bold prose-h5:mb-[0.1em] prose-h5:mt-0 prose-h5:text-inherit",
        "prose-p:mb-[0.1em] prose-p:mt-0 prose-p:text-inherit",
        // Links are underlined (WCAG 1.4.1). A block with its own text colour (e.g. light text on a
        // dark footer) keeps links in that colour — a fixed blue would fail contrast there.
        (currentStyles.color || currentStyles.textColor) ? "prose-a:text-inherit" : "prose-a:text-blue-700",
        "prose-a:underline prose-a:underline-offset-2 hover:prose-a:decoration-2 font-medium transition-colors",
        "prose-ul:list-disc prose-ul:pl-5 prose-ul:mb-[0.1em] prose-ul:mt-0 prose-ul:text-inherit",
        "prose-ol:list-decimal prose-ol:pl-5 prose-ol:mb-[0.1em] prose-ol:mt-0 prose-ol:text-inherit",
        "prose-li:mb-0 prose-li:text-inherit",
        // Alignment inheritance for all tags
        "[&_*]:[text-align:inherit]"
      )}
      style={wrapperStyles as any}
    >
      {combinedCustomCss && <style>{combinedCustomCss}</style>}
      <div
        contentEditable={isAdmin}
        onBlur={handleBlur}
        suppressContentEditableWarning={true}
        key={block.content}
        className={cn(
          "w-full outline-none min-h-[1em]",
          isAdmin && "cursor-text focus:ring-1 focus:ring-blue-200/20 rounded-sm px-1 -mx-1"
        )}
        style={{ textAlign: 'inherit' } as any}
        dangerouslySetInnerHTML={{ __html: localizeHtml(safeSanitize(resolveTags(block.content || ''))) }}
      />
    </div>
  );
}
